import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useData } from '../DataContext';
import imageCompression from 'browser-image-compression';
import * as nsfwjs from 'nsfwjs';
import './ProfileSetup.css';

const CATEGORIES = [
  'music', 'movie', 'tv show', 'anime', 'video game', 'book', 'podcast',
  'artist', 'director', 'youtuber/streamer', 'hobby', 'tech',
  'art style/aesthetic', 'philosophy', 'other'
];

const EXAMPLE_TAGS = {
  'music': 'e.g. death grips, radiohead, aphex twin',
  'movie': 'e.g. blade runner, stalker, perfect blue',
  'tv show': 'e.g. twin peaks, breaking bad, bojack horseman',
  'anime': 'e.g. cowboy bebop, evangelion, serial experiments lain',
  'video game': 'e.g. elden ring, hollow knight, celeste',
  'book': 'e.g. dune, neuromancer, kafka',
  'podcast': 'e.g. radiolab, hardcore history',
  'artist': 'e.g. bjork, radiohead, slowdive',
  'director': 'e.g. lynch, tarkovsky, villeneuve',
  'youtuber/streamer': 'e.g. jerma, northernlion',
  'hobby': 'e.g. photography, hiking, cooking',
  'tech': 'e.g. linux, mechanical keyboards, self-hosting',
  'art style/aesthetic': 'e.g. vaporwave, brutalism, solarpunk',
  'philosophy': 'e.g. existentialism, stoicism, absurdism',
  'other': 'e.g. anything else you like'
};

const CONTACT_TYPES = [
  { value: 'discord', label: 'Discord', placeholder: 'username' },
  { value: 'instagram', label: 'Instagram', placeholder: '@username' },
  { value: 'twitter', label: 'Twitter/X', placeholder: '@username' },
  { value: 'telegram', label: 'Telegram', placeholder: '@username' },
  { value: 'snapchat', label: 'Snapchat', placeholder: 'username' },
  { value: 'email', label: 'Email', placeholder: 'email@example.com' },
  { value: 'phone', label: 'Phone', placeholder: '+1234567890' },
  { value: 'other', label: 'Other', placeholder: 'your contact info' }
];

const DEFAULT_AVATAR = '/img/default.png';

let nsfwModel = null;

const loadNSFWModel = async () => {
  if (!nsfwModel) {
    nsfwModel = await nsfwjs.load();
  }
  return nsfwModel;
};

const moderateImage = async (file) => {
  try {
    const model = await loadNSFWModel();
    const img = document.createElement('img');
    const url = URL.createObjectURL(file);
    
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = url;
    });
    
    const predictions = await model.classify(img);
    URL.revokeObjectURL(url);
    
    const porn = predictions.find(p => p.className === 'Porn');
    const hentai = predictions.find(p => p.className === 'Hentai');
    const sexy = predictions.find(p => p.className === 'Sexy');
    
    if ((porn && porn.probability > 0.6) || (hentai && hentai.probability > 0.6)) {
      return false;
    }
    if (sexy && sexy.probability > 0.8) {
      return false;
    }
    return true;
  } catch (error) {
    console.error('NSFW detection error:', error);
    return true;
  }
};

const sanitize = (str) => str.replace(/[<>]/g, '');

export default function ProfileSetup({ session, onComplete, onBack, isFirstTime = true }) {
  const { getMyProfile, getMyTags, clearCache } = useData();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const [contactType, setContactType] = useState('discord');
  const [contactInfo, setContactInfo] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(CATEGORIES[0]);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        // Check if user exists, create if not
        let userData = await getMyProfile(session.user.email);
        
        if (!userData) {
          await supabase.from('users').insert({
            email: session.user.email,
            username: null,
            bio: null,
            profile_image: null,
            contact_info: null
          });
          userData = await getMyProfile(session.user.email, true);
        }

        if (userData) {
          setUsername(userData.username || '');
          setBio(userData.bio || '');
          setProfileImage(userData.profile_image);
          
          const contact = userData.contact_info || '';
          if (contact.includes(':')) {
            const [type, value] = contact.split(':').map(s => s.trim());
            const matchedType = CONTACT_TYPES.find(t => t.label.toLowerCase() === type.toLowerCase());
            if (matchedType) {
              setContactType(matchedType.value);
              setContactInfo(value);
            } else {
              setContactInfo(contact);
            }
          } else {
            setContactInfo(contact);
          }
        }

        const tagsData = await getMyTags(session.user.email);
        setTags(tagsData);
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [session.user.email, getMyProfile, getMyTags]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);

      const isSafe = await moderateImage(file);
      if (!isSafe) {
        alert('This image appears to contain inappropriate content. Please upload a different profile picture.');
        return;
      }

      const options = {
        maxSizeMB: 0.05,
        maxWidthOrHeight: 64,
        useWebWorker: true,
        fileType: 'image/webp'
      };

      const compressedFile = await imageCompression(file, options);
      const fileName = `${session.user.email}-${Date.now()}.webp`;
      const filePath = `profile-images/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('profile-images')
        .upload(filePath, compressedFile, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('profile-images')
        .getPublicUrl(filePath);

      setProfileImage(publicUrl);

      await supabase.from('users').upsert({
        email: session.user.email,
        profile_image: publicUrl
      });

      clearCache();
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const addTag = async () => {
    const tagText = newTag.trim().toLowerCase();
    
    if (!tagText) return;
    if (tags.some(t => t.tag === tagText && t.category === selectedCategory)) {
      alert('tag already added');
      return;
    }
    if (tags.length >= 50) {
      alert('maximum 50 tags');
      return;
    }

    try {
      const { error } = await supabase.from('user_tags').insert({
        user_email: session.user.email,
        tag: tagText,
        category: selectedCategory,
      });

      if (error) throw error;

      setTags([...tags, { tag: tagText, category: selectedCategory }]);
      setNewTag('');
      clearCache();
    } catch (error) {
      alert(error.message);
    }
  };

  const removeTag = async (tag, category) => {
    try {
      const { error } = await supabase
        .from('user_tags')
        .delete()
        .eq('user_email', session.user.email)
        .eq('tag', tag)
        .eq('category', category);

      if (error) throw error;

      setTags(tags.filter(t => !(t.tag === tag && t.category === category)));
      clearCache();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleSave = async () => {
    if (!username.trim() || tags.length === 0) {
      alert(username.trim() ? 'add at least one tag' : 'username required');
      return;
    }

    try {
      setSaving(true);

      const selectedContactType = CONTACT_TYPES.find(t => t.value === contactType);
      const formattedContact = contactInfo.trim() 
        ? `${selectedContactType?.label || 'Contact'}: ${contactInfo.trim()}`
        : '';

      const { error } = await supabase.from('users').upsert({
        email: session.user.email,
        username: username.trim(),
        bio: bio.trim(),
        contact_info: formattedContact,
        profile_image: profileImage
      });

      if (error) throw error;

      clearCache();
      if (onComplete) onComplete();
    } catch (error) {
      alert(error.message);
    } finally {
      setSaving(false);
    }
  };

  const tagsByCategory = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = tags.filter(t => t.category === cat);
    return acc;
  }, {});

  const previewSharedTags = tags.slice(0, 5);
  const previewOtherTags = tags.slice(5, 15);

  if (loading) {
    return <div className="loading">loading...</div>;
  }

  return (
    <div className="profile-setup">
      <div className="profile-setup-container">
        {onBack && (
          <button className="back-btn" onClick={onBack}>← back</button>
        )}
        
        <h1>{isFirstTime ? 'setup your profile' : 'edit profile'}</h1>

        <div className="preview-section">
          <label>preview (how others see you)</label>
          <div className="user-card-preview">
            <div 
              className="avatar" 
              style={{ 
                backgroundImage: profileImage 
                  ? `url(${profileImage})` 
                  : `url(${DEFAULT_AVATAR})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            />
            <div className="content">
              <div className="header">
                <div className="username">{username || 'username'}</div>
                <div className="match">??%</div>
              </div>
              
              {bio && <div className="bio">{bio}</div>}
              
              <div className="tags-section">
                {previewSharedTags.length > 0 && (
                  <div>
                    <div className="tags-label">your tags</div>
                    <div className="tags-row">
                      {previewSharedTags.map(t => (
                        <span key={`${t.tag}-${t.category}`} className="tag">
                          {t.tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {previewOtherTags.length > 0 && (
                  <div>
                    <div className="tags-row">
                      {previewOtherTags.map(t => (
                        <span key={`${t.tag}-${t.category}`} className="tag small">
                          {t.tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {tags.length === 0 && (
                  <div className="empty-preview">add tags below to see preview</div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="section">
          <label>username</label>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(sanitize(e.target.value))}
            placeholder="your username"
            maxLength={20}
          />
        </div>

        <div className="section">
          <label>bio (optional)</label>
          <textarea
            value={bio}
            onChange={(e) => setBio(sanitize(e.target.value))}
            placeholder="tell us about yourself..."
            maxLength={200}
            rows={3}
          />
          <div className="char-count">{bio.length}/200</div>
        </div>

        <div className="section">
          <label>contact info</label>
          <div className="contact-input">
            <select value={contactType} onChange={(e) => setContactType(e.target.value)}>
              {CONTACT_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            <input
              type="text"
              value={contactInfo}
              onChange={(e) => setContactInfo(sanitize(e.target.value))}
              placeholder={CONTACT_TYPES.find(t => t.value === contactType)?.placeholder || ''}
              maxLength={100}
            />
          </div>
          <p className="hint">This will be shared when someone pokes you and you accept.</p>
        </div>

        <div className="section">
          <label>profile picture (optional)</label>
          <div className="image-upload">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              id="image-upload"
              disabled={uploading}
            />
            <label htmlFor="image-upload" className="upload-label">
              {uploading ? 'uploading...' : profileImage ? 'change image' : 'upload image'}
            </label>
            {profileImage && (
              <button onClick={() => setProfileImage(null)} className="remove-image-btn">
                remove
              </button>
            )}
          </div>
          <p className="hint">Images are moderated. Inappropriate content will be rejected.</p>
        </div>

        <div className="section">
          <label>tags ({tags.length}/50) - aim for at least 15-20</label>
          <div className="tag-input">
            <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(sanitize(e.target.value))}
              onKeyPress={(e) => e.key === 'Enter' && addTag()}
              placeholder={EXAMPLE_TAGS[selectedCategory]}
              maxLength={30}
            />
            <button onClick={addTag} className="add-btn">add</button>
          </div>
        </div>

        {tags.length > 0 && (
          <div className="section">
            <label>your tags</label>
            <div className="tags-list">
              {CATEGORIES.map(category => {
                const categoryTags = tagsByCategory[category];
                if (categoryTags.length === 0) return null;
                
                return (
                  <div key={category} className="category-group">
                    <div className="category-name">{category}</div>
                    <div className="tags">
                      {categoryTags.map(t => (
                        <span key={`${t.tag}-${t.category}`} className="tag">
                          {t.tag}
                          <button onClick={() => removeTag(t.tag, t.category)}>×</button>
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <button
          className="save-btn"
          onClick={handleSave}
          disabled={saving || !username.trim() || tags.length === 0}
        >
          {saving ? 'saving...' : isFirstTime ? 'continue' : 'save changes'}
        </button>
      </div>
    </div>
  );
}